// Remove this file after the Account_Provisioning_History release.
import {useCallback, useEffect} from 'react';

import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getAccountUserHistoryByType} from 'src/openapi/admin/apis/GetAccountUserHistoryByTypeApi';
import {useAppDispatch} from 'src/store/hooks';

import type {DailyUserCountByType} from 'src/openapi/admin/models/DailyUserCountByType';
import type {Error} from 'src/openapi/admin/models/Error';

const ACTION_SUCCESS = 'accountUserHistoryChartData/success' as const;
const ACTION_FAILURE = 'accountUserHistoryChartData/failure' as const;

type Props = {
    accountId: number,
    days?: number,
};

export const useLegacyAccountUserHistoryChartData = ({accountId, days = 90}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction: (signal: AbortSignal) => Promise<{
        type: string,
        payload: {
            data: DailyUserCountByType[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }> = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getAccountUserHistoryByType({accountId, days}, {signal}));

        if (response.payload?.error) {
            return {
                type: ACTION_FAILURE,
                payload: {
                    data: response.payload.data as Error,
                    success: false,
                    error: true,
                },
            };
        }

        const raw = (response.payload?.data ?? []) as Array<DailyUserCountByType & {date?: string, day?: string}>;
        const normalized: DailyUserCountByType[] = raw.map(row => ({
            ...row,
            day: row.day ?? row.date ?? '',
        }));

        return {
            type: ACTION_SUCCESS,
            payload: {
                data: normalized,
                success: true,
                error: false,
            },
        };
    }, [accountId, apiDispatch, days]);

    const {data, isLoading, loadingError, loadData, createAbortSignal} = useReadOnlyDataHook<DailyUserCountByType[]>({
        initValue: [],
        getDispatchAction,
        shouldLoadData: false,
    });

    useEffect(() => {
        loadData();
    }, [loadData]);

    return {
        rows: data,
        isLoading,
        loadingError,
        loadData,
        createAbortSignal,
    };
};
